import React, { isValidElement } from "react";
import { styled } from "@linaria/react";
import { color } from "..";
import Button from "../Button";
import { EDITABLE_FORM_CONTROL_CLASS } from "./EditableCell";
import {
  STICKY_BORDER_LEFT_CLASSNAME,
  STICKY_BORDER_RIGHT_CLASSNAME,
  VERTICAL_SEPARATOR_CLASSNAME,
} from "./Td";
import useObserverCallback from "../lib/hooks/useObserverCallback";
import t from "../i18n";
import type * as io from "io-ts";
import validateInterface from "../lib/validateInterface";
import { colors } from "../Theme";
import Spinner from "../Spinner";

const ACTIVE_CN = "active";

interface P<
  Data,
  Column extends string,
  Signature extends Record<Column, string | string[] | undefined>,
> {
  children: React.ReactNode;
  findDataByDataKey: (dataKey: string) => Data | undefined;
  editedDataKey: string | undefined;
  onClose: () => void;
  signature: io.Type<Signature>;
  onSubmit: (values: Signature, data: Data) => Promise<void>;
  dirty: boolean;
  setDirty: (dirty: boolean, column: null) => void;
  className?: string;
}

const PADDING_HEIGHT = 50;

const Form = styled.form`
  position: relative;

  :first-child {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }

  :last-child {
    border-bottom-left-radius: inherit;
    border-bottom-right-radius: inherit;
  }

  tr.${ACTIVE_CN} {
    .${STICKY_BORDER_LEFT_CLASSNAME}::before {
      bottom: -${PADDING_HEIGHT}px !important;
    }
    .${STICKY_BORDER_RIGHT_CLASSNAME}::before {
      bottom: -${PADDING_HEIGHT}px !important;
    }
    .${VERTICAL_SEPARATOR_CLASSNAME}::before {
      bottom: -${PADDING_HEIGHT}px !important;
    }
    padding-bottom: ${PADDING_HEIGHT}px;
    background-color: ${color.titanium50} !important;

    & > td {
      background-color: ${color.titanium50} !important;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  position: absolute;
  right: 5px;
  align-items: center;
  justify-content: center;

  button:first-of-type {
    background-color: ${color.hydrogen};
    margin-right: 8px;
  }
`;

function findTr(form: HTMLFormElement, editedDataKey: string) {
  const tr = form.querySelector<HTMLElement>(`tr[data-key="${editedDataKey}"]`);
  if (!tr) throw new Error(`Missing edited row, cannot find tr[data-key="${editedDataKey}"]`);
  return tr;
}

function TableForm<
  Data,
  Column extends string,
  Signature extends Record<Column, string | string[] | undefined>,
>({
  children,
  editedDataKey,
  onClose,
  findDataByDataKey,
  onSubmit,
  dirty,
  setDirty,
  signature,
  className,
}: P<Data, Column, Signature>) {
  const formRef = React.useRef<HTMLFormElement>(null);
  const actionsRef = React.useRef<HTMLDivElement>(null);
  const editedData = editedDataKey === undefined ? null : findDataByDataKey(editedDataKey);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<boolean>(false);

  // set dirty back to false after editedDataKey changes
  React.useLayoutEffect(() => () => setDirty(false, null), [editedDataKey, setDirty]);

  // close the editable row is the row is absent from the dom
  React.useLayoutEffect(() => {
    if (!editedDataKey) return;
    if (!editedData) onClose();
  }, [editedData, editedDataKey, onClose]);

  // 1. open/close rows when editedDataKey changes
  React.useLayoutEffect(() => {
    if (!editedDataKey) return;
    if (!formRef.current) throw new Error("Missing form");
    const tr = findTr(formRef.current, editedDataKey);
    window.requestAnimationFrame(() => {
      tr.classList.add(ACTIVE_CN);
    });
    return () => {
      window.requestAnimationFrame(() => {
        tr.classList.remove(ACTIVE_CN);
      });
    };
  }, [editedDataKey]);

  // 2. position buttons
  useObserverCallback(
    ({ target }) => {
      if (!editedDataKey) return;
      const actions = actionsRef.current;
      if (!actions) throw new Error("Missing actions ref");

      const tr = findTr(target, editedDataKey);
      const { offsetTop } = tr;
      const { height } = tr.getBoundingClientRect();
      const newTop = `${offsetTop + height - PADDING_HEIGHT}px`;
      if (actions.style.display === "block" && actions.style.top === newTop) return;
      window.requestAnimationFrame(() => {
        actions.style.display = "block";
        actions.style.top = newTop;
      });
    },
    [editedDataKey],
    formRef,
    { fireOnInit: true, debounced: false },
  );

  // 3. focus first field after editedDataKey changes if focus isn't inside a field already (see EditableCell focus handling)
  React.useLayoutEffect(() => {
    if (!editedDataKey) return;
    const form = formRef.current;
    if (!form) throw new Error("Missing form");
    window.setTimeout(() => {
      const tr = findTr(form, editedDataKey);
      if (tr.contains(document.activeElement)) return; // do not change focus if focus is already inside the row
      window.requestAnimationFrame(() => {
        (
          tr.querySelectorAll(`.${EDITABLE_FORM_CONTROL_CLASS} input`)[0] as HTMLElement | null
        )?.focus();
      });
    });
  }, [editedDataKey]);

  const handleSubmit = React.useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const formData = new FormData(e.currentTarget);

      if (!editedData) throw new Error(`Cannot find edited data ${editedDataKey}`);

      const erroredInput = [
        ...e.currentTarget.querySelectorAll(`.${EDITABLE_FORM_CONTROL_CLASS} .error`),
      ][0];

      if (erroredInput && erroredInput instanceof HTMLElement) {
        erroredInput.focus();
        return;
      }

      const result = {} as Partial<Record<Column, string | string[]>>;
      for (const pair of formData.entries()) {
        const multi = pair[0].endsWith("[]");
        const key = (pair[0].endsWith("[]") ? pair[0].slice(0, -2) : pair[0]) as Column;
        const newValue = pair[1];
        if (typeof newValue !== "string") throw new Error("String only");
        if (multi) {
          const existingValues = result[key];
          if (typeof existingValues === "string")
            throw new Error(`${key} has been used without [] before. Check form names`);
          if (existingValues === undefined) {
            result[key] = newValue === "" ? [] : [newValue];
          } else {
            if (newValue === "")
              throw new Error(
                `Key ${key} multi value has an empty value that is not alone. This should not happen. It should only happen to indicate empty lists`,
              );
            existingValues.push(newValue);
          }
        } else {
          if (result[key])
            throw new Error(
              `Key ${key} has single value and has already been set. Use ${key}[] as name for inputs if you need an array`,
            );
          result[key] = newValue;
        }
      }

      setSubmitting(true);
      try {
        await onSubmit(validateInterface(signature, result), editedData);
        onClose();
      } finally {
        setSubmitting(false);
      }
    },
    [editedData, editedDataKey, onClose, onSubmit, signature],
  );

  React.useEffect(() => {
    const form = formRef.current;
    if (!form) throw new Error("Missing form ref");

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    form.addEventListener("keydown", handleKeyDown);
    return () => form.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // do not propagate click events to the rest of the form or table
  const handleActionsClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  }, []);

  React.useLayoutEffect(() => {
    if (!formRef.current) return;

    const activeRow = formRef.current?.querySelector(`tr[data-key="${editedDataKey}"]`);
    if (!activeRow) return;

    const observer = new MutationObserver(() => {
      const hasErrorInputs =
        activeRow.querySelectorAll(`.${EDITABLE_FORM_CONTROL_CLASS} .error input`).length > 0;
      setError(hasErrorInputs);
    });

    observer.observe(activeRow, {
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, [editedDataKey]);

  if (!children || !isValidElement(children)) throw new Error("Child should be a Table element");
  return (
    <Form ref={formRef} noValidate onSubmit={handleSubmit} className={className}>
      {children}
      {editedData ? (
        <Actions ref={actionsRef} onClick={handleActionsClick}>
          <Button tabIndex={0} variant="tertiary" onClick={onClose} disabled={submitting}>
            {t("table.form.cancel")}
          </Button>
          <Button
            tabIndex={0}
            variant="primary"
            type="submit"
            disabled={submitting || !dirty || error}
            leftIcon={submitting ? <Spinner color={colors.hydrogen} /> : undefined}
          >
            {t("table.form.confirm")}
          </Button>
        </Actions>
      ) : null}
    </Form>
  );
}

export default React.memo(TableForm) as typeof TableForm;
